import React, { useState } from 'react';
import dynamic from 'next/dynamic';
import { track } from '../../services/analytics';
import { Discount, BaseGym } from '../../types/Gym';
import { User } from '../../types/authentication';
import { trackAddToCart } from '../../utils/tracking';
import { FormattedPrice } from '../FormattedPrice/FormattedPrice';
import { ProductDiscountMessage } from './ProductDiscountMessage';
import { Button } from '@husslehq/ui-library';

// dynamically load
import { DirectModalProps } from './DirectModal';
import { ModalProps } from '../Modal/Modal';
const Modal = dynamic<ModalProps>(() => import('../Modal/Modal').then((mod) => mod.Modal));
const DirectModal = dynamic<DirectModalProps>(() => import('./DirectModal').then((mod) => mod.DirectModal));

import styles from '../../components/PassPicker/Product.module.scss';

type ProductItemProps = {
  type: string;
  id: string;
  gym: BaseGym;
  price: number;
  originalPrice?: number;
  discount?: Discount;
  bullets: Array<{ item: string }>;
  recommended?: boolean;
  isTemporarilyClosed?: boolean;
  user?: User;
  hideIncludedGymsLink?: boolean;
  className?: string;
  purchasable: boolean;
  sourceSegments: string[];
};

export const ProductItem: React.FC<ProductItemProps> = ({
  type,
  id,
  gym,
  price,
  bullets,
  recommended,
  discount,
  isTemporarilyClosed,
  purchasable,
  originalPrice,
  user,
  hideIncludedGymsLink,
  className,
  sourceSegments,
}) => {
  const [showModal, setShowModal] = useState(false);
  const recommendedList = (items: any[]) => (
    <ul className={styles['product__list']}>
      {items.map((item, index) => (
        <li key={index}>
          <span className={styles['product__list-item']}>{item.item}</span>
        </li>
      ))}
    </ul>
  );

  const fullProductName = () => {
    switch (type) {
      case 'monthly':
        return 'Monthly+';
      case 'direct':
        return 'Gym Membership';
      case 'daypass':
        return 'Day Pass';
    }
  };

  const buyProduct = (productType: string, productId: string | undefined, gym: BaseGym) => {
    trackAddToCart(gym, productType);
    track({
      event: 'gym_spa',
      category: 'Fake Door',
      action: `${fullProductName()}`,
      label: `${gym.name}`,
    });
    track({
      event: 'gym_spa',
      category: 'Fake Door',
      action: `${fullProductName()}`,
      label: `${fullProductName()}`,
    });
    track({
      event: 'gym_spa',
      category: 'Add to cart - Gym details',
      action: `${fullProductName()}`,
      label: `${gym.name}`,
    });
    const clearBasket = ['direct', 'monthly'].includes(productType) ? 1 : 0;
    document.location.href = `/checkout/small-basket-item-add?clear=${clearBasket}&productId=${productId}${
      productType === 'monthly' ? `&productGymId=${gym.id}` : ''
    }`;
  };

  const hasMonthlyWithThisGym = type === 'monthly' && user && user.hasMonthly && user.monthlyGymId === gym.id;
  const hasMonthlyWithAnotherGym = type === 'monthly' && user && user.hasMonthly && user.monthlyGymId !== gym.id;

  const makeMainGym = () => {
    document.location.href = `/account/change-mplus-gym/${gym.id}`;
  };

  const openDirectModal = () => {
    track({
      event: 'gym_spa',
      category: 'Gym Details',
      action: 'pass card',
      label: 'how does it work',
    });
    setShowModal(true);
  };

  const closeDirectModal = () => {
    setShowModal(false);
  };

  const keyPoints = () => {
    switch (type) {
      case 'monthly':
        return 'Monthly, cancel anytime.';
      case 'daypass':
        return 'Single visit';
      case 'direct':
        return `Monthly, cancel anytime. ${`${
          originalPrice && originalPrice > price
            ? `£${originalPrice && (originalPrice / 100).toFixed(2)} after first month.`
            : ''
        }`}`;
    }
  };

  function productInfo() {
    if (!purchasable) {
      const productName = type === 'monthly' ? 'Monthly+' : 'Day Pass';
      let notAvailableMessage =
        productName +
        ' at this gym is only available to customers who are not part of our corporate programme or employee benefit scheme.';
      if (sourceSegments.includes('B2B Corporate')) {
        notAvailableMessage =
          productName +
          ' at this gym is exclusively available to employees whose workplaces offer a corporate fitness benefit with Hussle. If you would like to refer your workplace to access this gym, <a href="/employees">click here</a>.';
      } else if (sourceSegments.includes('B2B Commercial')) {
        notAvailableMessage =
          productName +
          ' at this gym is exclusively available to customers accessing it through one of our commercial partners (e.g. AXA, BUPA).';
      }
      return (
        <div className={styles.product__notpurchasable}>
          <p dangerouslySetInnerHTML={{ __html: notAvailableMessage }} />
        </div>
      );
    }

    const getPrice = () => {
      if (discount && (discount.discounted_price || discount.discounted_price === 0)) {
        if (type === 'monthly' && !discount.trial_days) return [discount.discounted_price, price];
        if (type === 'daypass') return [discount.discounted_price, price];
      }
      if (type === 'direct' && originalPrice && originalPrice > price) {
        return [price, originalPrice];
      }
      return [price];
    };

    const [priceValue, originalPriceValue] = getPrice();

    return (
      <div className={styles.product__info}>
        <FormattedPrice
          className={styles.product__price}
          classNamePounds={styles['product__price-pounds']}
          price={priceValue}
          originalPrice={originalPriceValue}
        />
        <p className={styles['product__key-points']}>{keyPoints()}</p>
      </div>
    );
  }

  return (
    <>
      {(type === 'monthly' || purchasable) && (
        <div
          className={`${styles.product__item} ${discount && styles['product--priority-order']} ${
            className ? styles[className] : ''
          }`}
        >
          <div data-testid={`product-${type}`} className={`${styles.product} ${styles[`product--${type}`]}`}>
            {recommended ? (
              <p className={styles.product__label}>Recommended</p>
            ) : (
              (discount || (originalPrice && originalPrice > price)) && <p className={styles.product__label}>Offer</p>
            )}
            <div className={styles.product__upper}>
              <h3 className={styles.product__name}>{fullProductName()}</h3>
              {recommendedList(bullets)}
              {type === 'monthly' && !hideIncludedGymsLink && (
                <a href={`/search/extra-gym?id=${gym.id}&pid=${id}`} className={styles['product__link']}>
                  See gyms included
                </a>
              )}
              {type === 'direct' && (
                <Button onClick={openDirectModal} level="link" className={styles['product__link']}>
                  How does it work?
                </Button>
              )}
            </div>

            {productInfo()}

            {hasMonthlyWithThisGym && <div className={styles.product__primaryGym}>This is your main gym</div>}
            {hasMonthlyWithAnotherGym && purchasable && (
              <Button
                onClick={makeMainGym}
                disabled={isTemporarilyClosed}
                level="primary"
                fullWidth
                className={`${styles['product__buy-button']} ${styles['product__make-main-button']}`}
              >
                Make this my main gym
              </Button>
            )}
            {!hasMonthlyWithAnotherGym && !hasMonthlyWithThisGym && purchasable && (
              <Button
                level="primary"
                disabled={isTemporarilyClosed || !purchasable}
                fullWidth
                className={styles['product__buy-button']}
                onClick={() => buyProduct(type, id, gym)}
              >
                {discount ? (
                  <ProductDiscountMessage discount={discount} productType={type} />
                ) : (
                  <>Choose {type !== 'direct' ? fullProductName() : 'Membership'}</>
                )}
              </Button>
            )}
            {type === 'direct' && (
              <Modal
                onRequestClose={closeDirectModal}
                visible={showModal}
                modalClassName="ReactModal__Content--noPadding"
              >
                <DirectModal
                  onCloseModal={closeDirectModal}
                  onBuyProduct={buyProduct}
                  productId={id}
                  gym={gym}
                  isTemporarilyClosed={isTemporarilyClosed}
                />
              </Modal>
            )}
          </div>
        </div>
      )}
    </>
  );
};
